import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigationStore } from '@auw/stores';
import type { Conversation, WorkspaceMode } from '@auw/workspace-utils';
import {
  buildWorkspaceItemPath,
  buildWorkspaceModePath,
} from './useWorkspaceRoute';
import { runAfterWorkspaceNavigationGuards } from './workspaceNavigationGuard';

interface NavigateOptions {
  replace?: boolean;
}

interface OpenWorkspaceItemOptions extends NavigateOptions {
  syncMode?: boolean;
}

const START_PAGE_MODES = new Set<WorkspaceMode>(['agent', 'live-agent', 'document']);

function findPreferredConversationForMode(
  conversations: Record<string, Conversation>,
  conversationOrder: string[],
  mode: WorkspaceMode,
): string | null {
  const preferNonShared = mode === 'agent';
  if (preferNonShared) {
    const nonShared = conversationOrder.find((id) => {
      const conversation = conversations[id];
      return conversation && conversation.mode === mode && !conversation.sharedGroup;
    });
    if (nonShared) return nonShared;
  }

  return conversationOrder.find((id) => {
    const conversation = conversations[id];
    return conversation?.mode === mode;
  }) ?? null;
}

export function useWorkspaceNavigate() {
  const navigate = useNavigate();
  const rememberModeSelection = useWorkspaceNavigationStore((s) => s.rememberModeSelection);
  const clearRememberedModeSelection = useWorkspaceNavigationStore((s) => s.clearRememberedModeSelection);
  const rememberedIdByMode = useWorkspaceNavigationStore((s) => s.rememberedIdByMode);
  const setActiveConversation = useChatStore((s) => s.setActiveConversation);
  const setActiveMode = useChatStore((s) => s.setActiveMode);
  const createConversation = useChatStore((s) => s.createConversation);
  const conversations = useChatStore((s) => s.conversations);
  const conversationOrder = useChatStore((s) => s.conversationOrder);

  const openWorkspaceItem = useCallback((conversationId: string, options?: OpenWorkspaceItemOptions) => {
    const conversation = useChatStore.getState().conversations[conversationId];
    if (!conversation) return false;

    runAfterWorkspaceNavigationGuards(() => {
      rememberModeSelection(conversation.mode, conversation.id);
      setActiveConversation(conversation.id, { syncMode: options?.syncMode });
      navigate(buildWorkspaceItemPath(conversation.mode, conversation.id), { replace: options?.replace });
    });
    return true;
  }, [navigate, rememberModeSelection, setActiveConversation]);

  const openWorkspaceDraft = useCallback((mode: WorkspaceMode, options?: NavigateOptions) => {
    runAfterWorkspaceNavigationGuards(() => {
      clearRememberedModeSelection(mode);
      setActiveMode(mode);
      setActiveConversation(null, { syncMode: false });
      navigate(buildWorkspaceModePath(mode), { replace: options?.replace });
    });
  }, [clearRememberedModeSelection, navigate, setActiveConversation, setActiveMode]);

  const openWorkspaceMode = useCallback((mode: WorkspaceMode, options?: NavigateOptions) => {
    const rememberedId = rememberedIdByMode[mode];
    if (rememberedId) {
      const rememberedConversation = conversations[rememberedId];
      if (rememberedConversation?.mode === mode) {
        const opened = openWorkspaceItem(rememberedId, options);
        if (opened) return;
      }
    }

    const existingId = findPreferredConversationForMode(conversations, conversationOrder, mode);
    if (existingId) {
      const opened = openWorkspaceItem(existingId, options);
      if (opened) return;
    }

    if (!START_PAGE_MODES.has(mode)) {
      runAfterWorkspaceNavigationGuards(() => {
        setActiveMode(mode);
        navigate(buildWorkspaceModePath(mode), { replace: options?.replace });
      });
      return;
    }

    openWorkspaceDraft(mode, options);
  }, [
    conversationOrder,
    conversations,
    navigate,
    openWorkspaceDraft,
    openWorkspaceItem,
    rememberedIdByMode,
    setActiveMode,
  ]);

  const openOrCreateWorkspaceMode = useCallback((mode: WorkspaceMode, options?: NavigateOptions) => {
    if (!START_PAGE_MODES.has(mode)) {
      openWorkspaceMode(mode, options);
      return;
    }

    const existingId = findPreferredConversationForMode(conversations, conversationOrder, mode);
    if (existingId) {
      openWorkspaceItem(existingId, options);
      return;
    }

    openWorkspaceDraft(mode, options);
  }, [conversationOrder, conversations, openWorkspaceDraft, openWorkspaceItem, openWorkspaceMode]);

  const createWorkspaceItem = useCallback((params?: Parameters<typeof createConversation>[0], options?: NavigateOptions) => {
    // Keep the historical synchronous id when no guard is mounted (the start
    // page relies on it). From an active DOCX editor the action is delayed, so
    // creation itself cannot unmount the editor before its async save finishes.
    let createdId: string | null = null;
    runAfterWorkspaceNavigationGuards(() => {
      const nextId = createConversation(params);
      createdId = nextId;
      const createdConversation = useChatStore.getState().conversations[nextId];
      if (createdConversation) {
        rememberModeSelection(createdConversation.mode, createdConversation.id);
        navigate(buildWorkspaceItemPath(createdConversation.mode, createdConversation.id), { replace: options?.replace });
      }
    });
    return createdId;
  }, [createConversation, navigate, rememberModeSelection]);

  const openPluginDetail = useCallback((pluginId?: string | null, options?: NavigateOptions) => {
    const search = pluginId ? `?plugin=${encodeURIComponent(pluginId)}` : '';
    runAfterWorkspaceNavigationGuards(() => {
      setActiveMode('plugin');
      navigate({
        pathname: buildWorkspaceModePath('plugin'),
        search,
      }, { replace: options?.replace });
    });
  }, [navigate, setActiveMode]);

  return {
    openWorkspaceItem,
    openWorkspaceDraft,
    openWorkspaceMode,
    openOrCreateWorkspaceMode,
    createWorkspaceItem,
    openPluginDetail,
  };
}
